import {assertHTMLElement, invariant} from '../../render/utils'

type ImageBox = {height: number; width: number}
type ScalingFn = {
  (imgBox: ImageBox): number
}

/**
 * The ImageDiffDimensioner resizes image nodes and their parent nodes to the largest
 * height and width of the two images being diffed.
 * Then, we determine and apply a scaling factor produced from the scalingFn argument.
 *
 * The Dimensioner returns references to the image and parent nodes, their dimensions, and
 * image box sizing.
 */
export const ImageDiffDimensioner = (added: HTMLImageElement, deleted: HTMLImageElement, scalingFn: ScalingFn) => {
  const getDimensions = (element: HTMLImageElement) => {
    return {
      height: element.clientHeight || 0,
      width: element.clientWidth || 0,
    }
  }
  invariant(assertHTMLElement(added))
  invariant(assertHTMLElement(deleted))

  const addedImageFrame = added.parentNode
  const deletedImageFrame = deleted.parentNode

  invariant(assertHTMLElement(addedImageFrame))
  invariant(assertHTMLElement(deletedImageFrame))

  const addedDimensions = getDimensions(added)
  const deletedDimensions = getDimensions(deleted)

  const imageBox = {
    height: Math.max(deletedDimensions.height, addedDimensions.height),
    width: Math.max(deletedDimensions.width, addedDimensions.width),
  }
  const imageMargins = {
    addedHorizMargin: Math.floor(imageBox.width - addedDimensions.width) / 2,
    addedVertMargin: Math.floor(imageBox.height - addedDimensions.height) / 2,
    deletedHorizMargin: Math.floor(imageBox.width - deletedDimensions.width) / 2,
    deletedVertMargin: Math.floor(imageBox.height - deletedDimensions.height) / 2,
  }

  const scale = scalingFn(imageBox)

  added.style.width = `${addedDimensions.width * scale}px`
  added.style.height = `${addedDimensions.height * scale}px`

  deleted.style.width = `${deletedDimensions.width * scale}px`
  deleted.style.height = `${deletedDimensions.height * scale}px`

  addedImageFrame.setAttribute(
    'style',
    `margin: ${imageMargins.addedVertMargin * scale}px ${imageMargins.addedHorizMargin * scale}px;
    width: ${addedDimensions.width * scale + 2}px;
    height: ${addedDimensions.height * scale + 2}px;`,
  )

  deletedImageFrame.setAttribute(
    'style',
    `margin: ${imageMargins.deletedVertMargin * scale}px ${imageMargins.deletedHorizMargin * scale}px;
    width: ${deletedDimensions.width * scale + 2}px;
    height: ${deletedDimensions.height * scale + 2}px;`,
  )

  return {
    added: addedDimensions,
    deleted: deletedDimensions,
    imageMargins,
    scale,
    imageBox,
    addedImage: added,
    deletedImage: deleted,
    addedParent: addedImageFrame,
    deletedParent: deletedImageFrame,
  }
}
